/**
 * Form core for accommodations — the pure module owning the accommodation
 * **Draft**: its type, entry states, transitions, validation and the builders
 * that turn it into create/update wire payloads. No React, no DOM: the Add and
 * Edit modals render it and wire it to mutations, nothing more.
 *
 * Check-in/check-out are real **instants** (see `lib/datetime/instant.ts`), so
 * the Draft holds them as `datetime-local` input strings in the viewer's zone
 * and crosses to the UTC wire string only in the payload builders.
 *
 * The location box and the link rows are the shared pieces from `@lib/place`
 * and `@lib/links`, so a stay shows and sends them exactly as an idea or an
 * event does.
 */

import type { CreateAccommodationPayload } from '@hooks/useCreateAccommodation'
import type { UpdateAccommodationPayload } from '@hooks/useUpdateAccommodation'
import { dateToInput, inputToUtcString, utcStringToInput } from '@lib/datetime'
import { compactLinks } from '@lib/links'
import {
	emptyPlace,
	nameAfterPick,
	placeFromLocation,
	placeToPayload,
	placeTyped,
	type PlaceDraft,
} from '@lib/place'
import { countLabel } from '@/utils/plural'
import type { GetAccommodationResponse } from '@orbit/api-types'

/**
 * In-progress accommodation, not yet valid and not yet in wire format: dates are
 * `datetime-local` strings, `notes` is raw input text, and `guestIds` keeps
 * selection order (which is the order the payload sends).
 */
export interface AccommodationDraft {
	name: string
	location: PlaceDraft
	checkInDate: string
	checkOutDate: string
	notes: string
	/** One row per link, blanks included — the modals edit the rows with `@lib/links`. */
	links: string[]
	guestIds: Set<number>
}

/** A fresh stay: every member a guest, tonight to tomorrow. */
export const emptyAccommodationDraft = (
	memberIds: number[],
	now: Date = new Date(),
): AccommodationDraft => {
	const tomorrow = new Date(now)
	tomorrow.setDate(tomorrow.getDate() + 1)
	return {
		name: '',
		location: emptyPlace(),
		checkInDate: dateToInput(now),
		checkOutDate: dateToInput(tomorrow),
		notes: '',
		links: [],
		guestIds: new Set(memberIds),
	}
}

/** An existing stay, opened for editing. */
export const accommodationDraftFrom = (
	accommodation: GetAccommodationResponse,
): AccommodationDraft => ({
	name: accommodation.name,
	location: placeFromLocation(accommodation.location),
	checkInDate: utcStringToInput(accommodation.checkInDate),
	checkOutDate: utcStringToInput(accommodation.checkOutDate),
	notes: accommodation.notes ?? '',
	links: accommodation.links ?? [],
	guestIds: new Set(accommodation.guests.map(g => g.userId)),
})

// --- Transitions. Each returns a new Draft; none mutates its input. ---

export const setName = (draft: AccommodationDraft, name: string): AccommodationDraft => ({
	...draft,
	name,
})

/** Typing in the location box means the user is no longer on a chosen place. */
export const setLocationText = (draft: AccommodationDraft, text: string): AccommodationDraft => ({
	...draft,
	location: placeTyped(text),
})

/**
 * Picks a place from the search. `fillEmptyName` lets Add name the stay after
 * the place — when it has no name yet, or still carries the previous pick's —
 * while Edit never renames a stay behind the user's back.
 */
export const pickPlace = (
	draft: AccommodationDraft,
	place: PlaceDraft,
	{ fillEmptyName = false }: { fillEmptyName?: boolean } = {},
): AccommodationDraft => ({
	...draft,
	name: fillEmptyName ? nameAfterPick(draft.name, draft.location, place) : draft.name,
	location: place,
})

// Both ends are `YYYY-MM-DDTHH:MM`, so comparing the strings orders them exactly
// as comparing the instants would — no parsing, no zone to get wrong.
const isNotBefore = (a: string, b: string): boolean => Boolean(a) && Boolean(b) && a >= b

/** Sets check-in, clearing a check-out that would no longer come after it. */
export const setCheckIn = (draft: AccommodationDraft, checkInDate: string): AccommodationDraft => ({
	...draft,
	checkInDate,
	checkOutDate: isNotBefore(checkInDate, draft.checkOutDate) ? '' : draft.checkOutDate,
})

/** Sets check-out, clearing a check-in that would no longer come before it. */
export const setCheckOut = (draft: AccommodationDraft, checkOutDate: string): AccommodationDraft => ({
	...draft,
	checkOutDate,
	checkInDate: isNotBefore(draft.checkInDate, checkOutDate) ? '' : draft.checkInDate,
})

export const toggleGuest = (draft: AccommodationDraft, userId: number): AccommodationDraft => {
	const guestIds = new Set(draft.guestIds)
	if (!guestIds.delete(userId)) guestIds.add(userId)
	return { ...draft, guestIds }
}

export const setNotes = (draft: AccommodationDraft, notes: string): AccommodationDraft => ({
	...draft,
	notes,
})

// --- Validation ---

/**
 * What the modal needs to know to render: whether the Draft is submittable, and
 * the messages under the date row and the guest list. `canSubmit` is about the
 * Draft alone — a modal still ANDs it with "not already submitting".
 */
export interface AccommodationValidation {
	canSubmit: boolean
	/** Shown under the date row once both ends are set but out of order. */
	dateError: string | null
	/** Shown under the guest list when nobody is staying. */
	guestError: string | null
	/** Shown under the guest list otherwise; `null` while `guestError` is set. */
	guestSummary: string | null
}

export const validateAccommodationDraft = (
	draft: AccommodationDraft,
): AccommodationValidation => {
	const datesOrdered =
		draft.checkInDate.length > 0 &&
		draft.checkOutDate.length > 0 &&
		draft.checkInDate < draft.checkOutDate
	const hasGuests = draft.guestIds.size > 0
	return {
		canSubmit: draft.name.trim().length > 0 && datesOrdered && hasGuests,
		dateError:
			draft.checkInDate && draft.checkOutDate && !datesOrdered
				? 'Check-out must be after check-in'
				: null,
		guestError: hasGuests ? null : 'Select at least one guest',
		guestSummary: hasGuests ? `${countLabel(draft.guestIds.size, 'guest')} selected` : null,
	}
}

// --- Payload builders: where the Draft crosses into wire format ---

const sharedFields = (draft: AccommodationDraft) => ({
	name: draft.name.trim(),
	checkInDate: inputToUtcString(draft.checkInDate),
	checkOutDate: inputToUtcString(draft.checkOutDate),
	guestUserIds: Array.from(draft.guestIds),
	notes: draft.notes.trim() || null,
})

/** POST body for a new stay: fields with nothing in them are left out. */
export const toCreatePayload = (draft: AccommodationDraft): CreateAccommodationPayload => {
	const links = compactLinks(draft.links)
	return {
		...sharedFields(draft),
		links: links.length > 0 ? links : undefined,
		location: placeToPayload(draft.location),
	}
}

/**
 * PATCH body for an existing stay. The modal edits every field at once, so the
 * update is a full replacement: it never omits a field, and an optional the user
 * emptied is *cleared* with an explicit `null`/`[]` rather than dropped — omitting
 * it would leave the old value in place.
 */
export const toUpdatePayload = (draft: AccommodationDraft): UpdateAccommodationPayload => ({
	...sharedFields(draft),
	links: compactLinks(draft.links),
	location: placeToPayload(draft.location) ?? null,
})
